import { CommonSubmissionItemCardProps, SubmissionItemCard } from '@/components/AppStoreItems/AppStoreItemsCard';
import { antGrey } from '@/components/styles/globalStyles';
import { formatDateWithTimezone } from '@/timeHelpers';
import { AppEventAssetType, AppStoreReviewSubmissionItemType, InAppEvent } from '@/types';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectCadenceTimezone } from '@/selectors/selectors';

export interface InAppEventCardProps extends CommonSubmissionItemCardProps {
    inAppEvent: InAppEvent;
}

export const InAppEventCard: React.FC<InAppEventCardProps> = ({ inAppEvent, ...rest }) => {
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, rest.appId));
    const cadenceTimezone = useSelector((state: RootState) => selectCadenceTimezone(state, rest.appId));

    return (
        <SubmissionItemCard item={inAppEvent} type={AppStoreReviewSubmissionItemType.AppEvent} {...rest}>
            <Text style={{ fontWeight: 400, color: antGrey.grey9, fontSize: 14 }}>{inAppEvent.referenceName}</Text>
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    width: '100%',
                    flexWrap: 'wrap',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: 6,
                        height: 149,
                        alignItems: 'start',
                    }}
                >
                    {inAppEvent.previewImages.map((image, index) =>
                        image.type === AppEventAssetType.EventCard ?
                            <img
                                key={index}
                                src={image.url}
                                style={{
                                    maxHeight: 66,
                                    borderRadius: 4,
                                }}
                            />
                        :   <img
                                key={index}
                                src={image.url}
                                style={{
                                    maxHeight: 149,
                                    borderRadius: 4,
                                }}
                            />,
                    )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20,
                        flex: 1,
                    }}
                >
                    <Text style={{ fontWeight: 400, color: antGrey.grey7 }}>{inAppEvent.shortDescription}</Text>
                    {inAppEvent.territorySchedules.length > 0 && (
                        <Space
                            split={<Divider type="vertical" style={{ backgroundColor: antGrey.grey3, height: 44 }} />}
                        >
                            <EventInfo
                                title="Publish start"
                                date={inAppEvent.territorySchedules[0].publishStart}
                                cadenceTimezone={cadenceTimezone}
                                use24HourTimeFormat={use24HourTimeFormat}
                            />
                            <EventInfo
                                title="Start"
                                date={inAppEvent.territorySchedules[0].eventStart}
                                cadenceTimezone={cadenceTimezone}
                                use24HourTimeFormat={use24HourTimeFormat}
                            />
                            <EventInfo
                                title="End"
                                date={inAppEvent.territorySchedules[0].eventEnd}
                                cadenceTimezone={cadenceTimezone}
                                use24HourTimeFormat={use24HourTimeFormat}
                            />
                        </Space>
                    )}
                </div>
            </div>
        </SubmissionItemCard>
    );
};

const EventInfo: React.FC<{ title: string; date: string; cadenceTimezone: string; use24HourTimeFormat: boolean }> = ({
    title,
    date,
    cadenceTimezone,
    use24HourTimeFormat,
}) => {
    return (
        <Space direction="vertical">
            <Text
                style={{
                    fontSize: 12,
                    color: antGrey.grey7,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    whiteSpace: 'nowrap',
                }}
            >
                {title}
            </Text>
            <Text style={{ fontSize: 14, color: antGrey.grey9 }}>
                {formatDateWithTimezone(date, 'timezone', cadenceTimezone, use24HourTimeFormat)}
            </Text>
        </Space>
    );
};
